import { useRef, useState } from "react";
import { ApiError, api, readJson } from "./api";
import { uuid } from "./uuid";
import type { CommandResult, MaterialCommand, TrimRoomState, TrimRoomSettings } from "@trimlog/trim-room-contract";

type WithoutCommandId<T> = T extends unknown ? Omit<T, "commandId"> : never;
export type CommandDraft = WithoutCommandId<MaterialCommand>;

export const fetchTrimRoom = (batchId?: string) => api.get(`/api/trim-room/state${batchId ? `?batchId=${encodeURIComponent(batchId)}` : ""}`)
  .then(r => readJson<TrimRoomState>(r));
export const executeCommand = (command: MaterialCommand) => api.postJson("/api/trim-room/commands", command)
  .then(r => readJson<CommandResult>(r));
export const saveTrimRoomSettings = (settings: TrimRoomSettings) => api.patchJson("/api/trim-room/settings", settings)
  .then(r => readJson<TrimRoomSettings>(r));

function retryKey(draft: CommandDraft): string {
  return JSON.stringify(draft, (key, value) => key === "expectedRevision" || key === "measuredAtMs" ? undefined : value);
}

/** An uncertain response reuses the exact command, including measurements and revision. */
export function useMaterialAction(refresh: () => Promise<unknown>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const attempt = useRef<{ key: string; command: MaterialCommand } | null>(null);
  async function run(draft: CommandDraft): Promise<CommandResult | null> {
    if (inFlight.current) return null;
    if (!navigator.onLine) { setError("Reconnect before issuing, receiving or reviewing material. Your timer can keep running offline."); return null; }
    const key = retryKey(draft);
    if (!attempt.current || attempt.current.key !== key) attempt.current = { key, command: { ...draft, commandId: uuid() } as MaterialCommand };
    inFlight.current = true; setBusy(true); setError("");
    try {
      const result = await executeCommand(attempt.current.command);
      attempt.current = null;
      await refresh().catch(() => {});
      return result;
    } catch (e) {
      if (e instanceof ApiError && e.status < 500) attempt.current = null;
      setError(e instanceof Error ? e.message : "The record could not be saved. Check the connection and retry.");
      if (e instanceof ApiError && e.status === 409) await refresh().catch(() => {});
      return null;
    } finally { inFlight.current = false; setBusy(false); }
  }
  return { run, busy, error, setError };
}

export async function downloadBatchCsv(batchId: string, lotReference: string): Promise<void> {
  const response = await api.get(`/api/trim-room/batches/${encodeURIComponent(batchId)}/export.csv`);
  if (!response.ok) await readJson(response);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = `trim-batch-${lotReference.replace(/[^a-zA-Z0-9_-]/g, "_")}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
